// apps/nos/client/src/services/vertexRecommendations_api.ts
import { serverURL } from "@/utils/serverURL";

// Wire types — mirror VertexRecommendationsResponse in
// apps/nos/server/src/controllers/me/flavor/formulas/versions/viz/vertexRecommendations.ts.
// Keep in lock-step when the server shape changes.

export type VertexRecommendationRow = {
  compoundId: string;
  kind: "compound";
  substanceId: number;
  name: string;
  family: { code: string; label: string; color: string } | null;
  improvement: number;
  penalty: number;
  score: number;
  deltaLoss: number;
  /** DEMO (impl-demo-mock) — pre-swap natural candidate id for scripted rows;
   *  the client mocks the viz/loss move from this original ingredient. Absent
   *  on natural rows. Remove with services/demo. */
  origSubstanceId?: number;
};

export type VertexRecommendationsResponse = {
  rows: VertexRecommendationRow[];
};

export type VertexRecommendationsRequest =
  | { scope: "detail"; dimIdx: number; k?: number }
  | { scope: "receptor"; dimIdxs: number[]; k?: number };

export class VertexRecommendationsApiError extends Error {
  status: number;
  code: string | null;
  constructor(status: number, code: string | null) {
    super(`vertex-recommendations failed: ${status} ${code ?? ""}`);
    this.name = "VertexRecommendationsApiError";
    this.status = status;
    this.code = code;
  }
}

export async function fetchVertexRecommendations(
  flavorId: string,
  formulaId: number,
  versionId: number,
  body: VertexRecommendationsRequest,
  signal?: AbortSignal,
  previewId?: string | null,
): Promise<VertexRecommendationsResponse> {
  const res = await fetch(
    serverURL`/me/flavor/${flavorId}/formulas/${formulaId}/versions/${versionId}/viz/vertex-recommendations`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(
        previewId != null ? { ...body, previewId } : body,
      ),
      ...(signal ? { signal } : {}),
    },
  );
  if (!res.ok) {
    let code: string | null = null;
    try {
      const data = (await res.json()) as { error?: string };
      code = data.error ?? null;
    } catch {
      /* ignore — non-JSON error body */
    }
    throw new VertexRecommendationsApiError(res.status, code);
  }
  return (await res.json()) as VertexRecommendationsResponse;
}
